import React, { useState } from "react";
import { Select, DatePicker, Radio, Tabs } from "antd";
import {
  CheckCircleOutlined,
  FileTextOutlined,
  FileDoneOutlined,
  DashboardOutlined,
} from "@ant-design/icons";
import RequirementVerificationTab from "./tabs/RequirementVerificationTab";
import MultireportSLATab from "./tabs/MultireportSLATab";
import ContractSLATab from "./tabs/ContractSLATab";
import Dashboard from "../dashboard/Dashboard";

const OPERATION_TAB = "operation";

const { Option } = Select;
const { RangePicker } = DatePicker;

const SLADashboard: React.FC = () => {
  const [period, setPeriod] = useState<string>("last_30_days");
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [filterType, setFilterType] = useState<"preset" | "custom">("preset");
  const [activeTab, setActiveTab] = useState<string>("requirements");

  const handlePeriodChange = (value: string) => {
    setPeriod(value);
  };

  const handleDateRangeChange = (
    _dates: any,
    dateStrings: [string, string]
  ) => {
    setDateRange(dateStrings);
  };

  const handleFilterTypeChange = (e: any) => {
    const newFilterType = e.target.value;
    setFilterType(newFilterType);

    if (newFilterType === "preset") {
      setDateRange(null);
    } else {
      setDateRange(null);
    }
  };

  const tabItems = [
    {
      key: "requirements",
      label: (
        <span className="flex items-center gap-2">
          <CheckCircleOutlined />
          Verificación de Requisitos
        </span>
      ),
      children: (
        <RequirementVerificationTab
          period={period}
          dateRange={dateRange}
          filterType={filterType}
        />
      ),
    },
    {
      key: "multireport",
      label: (
        <span className="flex items-center gap-2">
          <FileTextOutlined />
          Entrega de Multireport
        </span>
      ),
      children: (
        <MultireportSLATab
          period={period}
          dateRange={dateRange}
          filterType={filterType}
        />
      ),
    },
    {
      key: "contract",
      label: (
        <span className="flex items-center gap-2">
          <FileDoneOutlined />
          Generación de Contrato
        </span>
      ),
      children: (
        <ContractSLATab
          period={period}
          dateRange={dateRange}
          filterType={filterType}
        />
      ),
    },
    {
      key: OPERATION_TAB,
      label: (
        <span className="flex items-center gap-2">
          <DashboardOutlined />
          Operación
        </span>
      ),
      // Dashboard carries its own period/user filters (and a different period
      // vocabulary than the SLA tabs), so the global filter bar is hidden while
      // this tab is active. antd only mounts a pane once it is first activated,
      // so the /metrics/summary + /metrics/detailed aggregates are not
      // requested unless someone actually opens this tab.
      children: <Dashboard />,
    },
  ];

  return (
    <div className="bg-gray-50 min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-4 md:mb-0">
            Métricas
          </h1>

          {/* Global Filter Controls — SLA tabs only; Operación has its own */}
          {activeTab !== OPERATION_TAB && (
            <div className="flex flex-wrap items-center gap-2">
              <Radio.Group
                onChange={handleFilterTypeChange}
                value={filterType}
                className="mr-2"
              >
                <Radio.Button value="preset">Predefinido</Radio.Button>
                <Radio.Button value="custom">Custom</Radio.Button>
              </Radio.Group>

              {filterType === "preset" ? (
                <Select
                  defaultValue="last_30_days"
                  className="w-36"
                  onChange={handlePeriodChange}
                  value={period}
                >
                  <Option value="last_7_days">Últimos 7 Días</Option>
                  <Option value="last_30_days">Últimos 30 Días</Option>
                  <Option value="last_90_days">Últimos 90 Días</Option>
                </Select>
              ) : (
                <RangePicker
                  onChange={handleDateRangeChange}
                  className="w-auto"
                />
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow">
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            className="sla-dashboard-tabs"
            tabBarStyle={{
              paddingLeft: 16,
              paddingRight: 16,
              marginBottom: 0,
            }}
            tabBarGutter={32}
          />
        </div>
      </div>
    </div>
  );
};

export default SLADashboard;
